import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { styles } from "../shared/styles";
import { BackButton } from "../shared/BackButton";
import {
  LuPlus,
  LuFileDown,
  LuFolderOpen,
  LuClipboardCheck,
  LuPencil,
  LuTrash2,
  LuPaperclip,
  LuUpload,
  LuCamera,
} from "react-icons/lu";
import { generateGeneralQuotePDF } from "../generateGeneralQuotePDF";
import { useIsMobile } from "../shared/utils";

const C = {
  navy: "#1e3a5f",
  teal: "#0891b2",
  border: "#e2e8f0",
  bg: "#f8fafc",
  white: "#ffffff",
  slate: "#64748b",
  green: "#16a34a",
  red: "#e63946",
};

const STATUS_COLORS = {
  draft: { bg: "#f1f5f9", color: "#64748b" },
  sent: { bg: "#eff6ff", color: "#1d4ed8" },
  approved: { bg: "#f0fdf4", color: "#16a34a" },
  rejected: { bg: "#fff1f2", color: "#e11d48" },
  pending: { bg: "#fff7ed", color: "#ea580c" },
  in_progress: { bg: "#eff6ff", color: "#1d4ed8" },
  completed: { bg: "#f0fdf4", color: "#16a34a" },
};

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <span
      style={{
        backgroundColor: s.bg,
        color: s.color,
        border: `1px solid ${s.color}40`,
        borderRadius: "999px",
        padding: "2px 10px",
        fontSize: "11px",
        fontWeight: "600",
        textTransform: "capitalize",
        whiteSpace: "nowrap",
      }}
    >
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}

function btn(color, outline = false) {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: outline ? C.white : color,
    color: outline ? color : C.white,
    border: `1.5px solid ${color}`,
    borderRadius: "6px",
    padding: "8px 14px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };
}

const fmt2 = (n) =>
  Number(n).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const lineTotal = (qty, unitPrice) =>
  (parseFloat(qty) || 0) * (parseFloat(unitPrice) || 0);

const emptyItem = () => ({ description: "", proposed_solution: "", quantity: "1", unit_price_ex_gst: "", pendingPhotos: [] });

// ── Quote Form ────────────────────────────────────────────────────────────────

function QuoteForm({ sites, onSaved, onCancel }) {
  const [siteId, setSiteId] = useState("");
  const [title, setTitle] = useState("");
  const [items, setItems] = useState([emptyItem()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const totalEx = items.reduce((a, v) => a + lineTotal(v.quantity, v.unit_price_ex_gst), 0);
  const gst = totalEx * 0.1;
  const totalInc = totalEx * 1.1;

  const setItem = (idx, field, val) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: val } : it)));

  const handleSave = async () => {
    if (!siteId) { setError("Please select a site."); return; }
    if (items.every((it) => !it.description.trim())) { setError("Add at least one item."); return; }
    setSaving(true); setError("");
    try {
      const quoteRes = await axios.post(`${API}/general-quotes/`, {
        site: Number(siteId),
        title,
      });
      const quoteId = quoteRes.data.id;
      for (const item of items.filter((it) => it.description.trim())) {
        const qty = parseFloat(item.quantity) || 1;
        const unitPrice = parseFloat(item.unit_price_ex_gst) || 0;
        const itemRes = await axios.post(`${API}/general-quote-items/`, {
          quote: quoteId,
          description: item.description,
          proposed_solution: item.proposed_solution,
          quantity: qty,
          unit_price_ex_gst: unitPrice,
          price_ex_gst: qty * unitPrice,
        });
        for (const file of (item.pendingPhotos || [])) {
          const fd = new FormData();
          fd.append("photo", file);
          fd.append("item", itemRes.data.id);
          await axios.post(`${API}/general-quote-item-photos/`, fd);
        }
      }
      onSaved(quoteId);
    } catch (e) {
      const d = e.response?.data;
      if (!d) setError("Save failed.");
      else if (typeof d === "string") setError(d);
      else if (d.detail) setError(d.detail);
      else setError(Object.entries(d).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`).join(" | "));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
        <BackButton onClick={onCancel} />
        <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: C.navy }}>New Quotation</h2>
      </div>

      <div style={{ display: "flex", gap: "16px", marginBottom: "16px", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 220px" }}>
          <label style={{ fontSize: "13px", fontWeight: "600", color: C.navy, display: "block", marginBottom: "6px" }}>Site *</label>
          <select
            value={siteId}
            onChange={(e) => setSiteId(e.target.value)}
            style={{ width: "100%", padding: "8px 10px", border: `1px solid ${C.border}`, borderRadius: "6px", fontSize: "13px" }}
          >
            <option value="">— Select site —</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>{s.site_name}{s.account_name ? ` (${s.account_name})` : ""}</option>
            ))}
          </select>
        </div>
        <div style={{ flex: "2 1 300px" }}>
          <label style={{ fontSize: "13px", fontWeight: "600", color: C.navy, display: "block", marginBottom: "6px" }}>Quote Description / Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Annual Fire Equipment Maintenance 2026"
            style={{ width: "100%", padding: "8px 10px", border: `1px solid ${C.border}`, borderRadius: "6px", fontSize: "13px" }}
          />
        </div>
      </div>

      {/* Line Items */}
      <div style={{ overflowX: "auto", marginBottom: "16px" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ backgroundColor: C.navy, color: C.white }}>
              <th style={{ padding: "8px 10px", textAlign: "left" }}>#</th>
              <th style={{ padding: "8px 10px", textAlign: "left", minWidth: "180px" }}>Description</th>
              <th style={{ padding: "8px 10px", textAlign: "left", minWidth: "150px" }}>Proposed Solution</th>
              <th style={{ padding: "8px 10px", textAlign: "right", minWidth: "60px" }}>Qty</th>
              <th style={{ padding: "8px 10px", textAlign: "right", minWidth: "110px" }}>Unit Price (ex. GST)</th>
              <th style={{ padding: "8px 10px", textAlign: "right", minWidth: "90px" }}>GST</th>
              <th style={{ padding: "8px 10px", textAlign: "right", minWidth: "110px" }}>Total (inc. GST)</th>
              <th style={{ padding: "8px 10px" }}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => {
              const ex = lineTotal(item.quantity, item.unit_price_ex_gst);
              const itemGst = ex * 0.1;
              const itemTotal = ex * 1.1;
              return (
                <React.Fragment key={idx}>
                <tr style={{ backgroundColor: idx % 2 === 0 ? C.white : C.bg }}>
                  <td style={{ padding: "10px", color: C.slate, verticalAlign: "top" }}>{idx + 1}</td>
                  <td style={{ padding: "10px", verticalAlign: "top" }}>
                    <textarea
                      value={item.description}
                      onChange={(e) => setItem(idx, "description", e.target.value)}
                      placeholder="Describe the work…"
                      rows={3}
                      style={{ width: "100%", padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: "4px", fontSize: "12px", resize: "vertical" }}
                    />
                  </td>
                  <td style={{ padding: "10px", verticalAlign: "top" }}>
                    <textarea
                      value={item.proposed_solution}
                      onChange={(e) => setItem(idx, "proposed_solution", e.target.value)}
                      placeholder="Proposed approach…"
                      rows={3}
                      style={{ width: "100%", padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: "4px", fontSize: "12px", resize: "vertical" }}
                    />
                  </td>
                  <td style={{ padding: "10px", verticalAlign: "top" }}>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.quantity}
                      onChange={(e) => setItem(idx, "quantity", e.target.value)}
                      style={{ width: "60px", padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: "4px", fontSize: "13px", textAlign: "right" }}
                    />
                  </td>
                  <td style={{ padding: "10px", verticalAlign: "top" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "4px", justifyContent: "flex-end" }}>
                      <span style={{ color: C.slate }}>$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price_ex_gst}
                        onChange={(e) => setItem(idx, "unit_price_ex_gst", e.target.value)}
                        placeholder="0.00"
                        style={{ width: "80px", padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: "4px", fontSize: "13px", textAlign: "right" }}
                      />
                    </div>
                  </td>
                  <td style={{ padding: "10px", textAlign: "right", verticalAlign: "top", color: C.slate, whiteSpace: "nowrap" }}>
                    ${fmt2(itemGst)}
                  </td>
                  <td style={{ padding: "10px", textAlign: "right", verticalAlign: "top", fontWeight: "600", color: C.navy, whiteSpace: "nowrap" }}>
                    ${fmt2(itemTotal)}
                  </td>
                  <td style={{ padding: "10px", verticalAlign: "top" }}>
                    {items.length > 1 && (
                      <button
                        onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}
                        style={{ background: "none", border: "none", cursor: "pointer", color: C.red, padding: "4px" }}
                      >
                        <LuTrash2 size={15} />
                      </button>
                    )}
                  </td>
                </tr>
                <tr style={{ backgroundColor: idx % 2 === 0 ? C.white : C.bg }}>
                  <td colSpan={8} style={{ padding: "4px 10px 10px 36px" }}>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                      {(item.pendingPhotos || []).map((file, pi) => (
                        <div key={pi} style={{ position: "relative", flexShrink: 0 }}>
                          <img
                            src={URL.createObjectURL(file)}
                            alt=""
                            style={{ width: "64px", height: "64px", objectFit: "cover", borderRadius: "4px", border: `1px solid ${C.border}`, display: "block" }}
                          />
                          <button
                            onClick={() => setItems((prev) => prev.map((it, i) => i === idx ? { ...it, pendingPhotos: it.pendingPhotos.filter((_, j) => j !== pi) } : it))}
                            style={{ position: "absolute", top: -5, right: -5, width: "16px", height: "16px", borderRadius: "50%", background: C.red, border: "none", color: C.white, fontSize: "11px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, lineHeight: 1 }}
                            title="Remove photo"
                          >×</button>
                        </div>
                      ))}
                      <label style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", color: C.slate, padding: "4px 8px", border: `1px dashed ${C.border}`, borderRadius: "4px", flexShrink: 0 }}>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const files = Array.from(e.target.files);
                            if (files.length) setItems((prev) => prev.map((it, i) => i === idx ? { ...it, pendingPhotos: [...(it.pendingPhotos || []), ...files] } : it));
                            e.target.value = "";
                          }}
                        />
                        <LuCamera size={13} /> Add Photo
                      </label>
                    </div>
                  </td>
                </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <button
        style={{ ...btn(C.teal, true), marginBottom: "20px" }}
        onClick={() => setItems((prev) => [...prev, emptyItem()])}
      >
        <LuPlus size={14} /> Add Item
      </button>

      {/* Totals */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "20px" }}>
        <div style={{ border: `1px solid ${C.border}`, borderRadius: "8px", overflow: "hidden", minWidth: "260px" }}>
          {[
            { label: "Subtotal (ex. GST)", value: `$${fmt2(totalEx)}` },
            { label: "GST (10%)", value: `$${fmt2(gst)}` },
          ].map(({ label, value }) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "8px 14px", borderBottom: `1px solid ${C.border}`, fontSize: "13px" }}>
              <span style={{ color: C.slate }}>{label}</span>
              <span style={{ color: C.navy, fontWeight: "600" }}>{value}</span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", backgroundColor: C.navy }}>
            <span style={{ color: C.white, fontWeight: "700", fontSize: "14px" }}>TOTAL (inc. GST)</span>
            <span style={{ color: C.white, fontWeight: "700", fontSize: "14px" }}>${fmt2(totalInc)}</span>
          </div>
        </div>
      </div>

      {error && <div style={{ color: C.red, fontSize: "13px", marginBottom: "12px" }}>{error}</div>}

      <div style={{ display: "flex", gap: "10px" }}>
        <button style={btn(C.slate, true)} onClick={onCancel}>Cancel</button>
        <button style={btn(C.navy)} onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save Quote"}
        </button>
      </div>
    </div>
  );
}

// ── Quote Detail ──────────────────────────────────────────────────────────────

function QuoteDetail({ quoteId, autoFile, onAutoFileDone, onBack, onRequestCreated }) {
  const [quote, setQuote] = useState(null);
  const [techs, setTechs] = useState([]);
  const [assignedTo, setAssignedTo] = useState("");
  const [creatingRequest, setCreatingRequest] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [filingPDF, setFilingPDF] = useState(false);
  const [showFileModal, setShowFileModal] = useState(false);
  const [siteFolders, setSiteFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editItems, setEditItems] = useState({});
  const [editNewItems, setEditNewItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const attachmentInputRef = useRef(null);

  const hasAutoFiled = useRef(false);

  const load = useCallback(() => {
    axios.get(`${API}/general-quotes/${quoteId}/`).then((r) => setQuote(r.data));
  }, [quoteId]);

  useEffect(() => {
    load();
    axios.get(`${API}/technicians/`).then((r) => setTechs(r.data));
  }, [load]);

  useEffect(() => {
    if (!quote || !autoFile || hasAutoFiled.current) return;
    hasAutoFiled.current = true;
    onAutoFileDone?.();
    setGeneratingPDF(true);
    axios.get(`${API}/site-folders/?site=${quote.site}`)
      .then((res) => {
        setSiteFolders(res.data || []);
        setSelectedFolderId("");
        setNewFolderName("");
        setShowFileModal(true);
      })
      .finally(() => setGeneratingPDF(false));
  }, [quote, autoFile]);

  if (!quote) return <div style={{ padding: "40px", color: C.slate }}>Loading…</div>;

  const handleItemPhotoUpload = async (itemId, e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("photo", file);
        fd.append("item", itemId);
        await axios.post(`${API}/general-quote-item-photos/`, fd);
      }
    } catch (err) {
      alert("Photo upload failed: " + err.message);
    } finally {
      e.target.value = "";
      load();
    }
  };

  const handleDeleteItemPhoto = async (photoId) => {
    try {
      await axios.delete(`${API}/general-quote-item-photos/${photoId}/`);
      load();
    } catch (err) {
      alert("Delete failed: " + err.message);
    }
  };

  const handleAttachmentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingAttachment(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("quote", quote.id);
        await axios.post(`${API}/general-quote-attachments/`, fd);
      }
    } catch (err) {
      alert("Upload failed: " + err.message);
    } finally {
      e.target.value = "";
      setUploadingAttachment(false);
      load();
    }
  };

  const handleDeleteAttachment = async (id) => {
    try {
      await axios.delete(`${API}/general-quote-attachments/${id}/`);
      load();
    } catch (err) {
      alert("Delete failed: " + err.message);
    }
  };

  const handlePDF = async () => {
    setGeneratingPDF(true);
    try {
      const res = await axios.get(`${API}/site-folders/?site=${quote.site}`);
      setSiteFolders(res.data || []);
      setSelectedFolderId("");
      setNewFolderName("");
      setShowFileModal(true);
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleDownload = async (fileToFolder) => {
    setFilingPDF(true);
    setShowFileModal(false);
    try {
      if (!fileToFolder) {
        await generateGeneralQuotePDF(quote);
        return;
      }
      const blob = await generateGeneralQuotePDF(quote, { noDownload: true });
      const siteSlug = (quote.site_name || "site").replace(/[^a-z0-9]/gi, "_");
      const fileName = `GQ-${String(quote.id).padStart(3, "0")}_${siteSlug}_Quotation.pdf`;

      let folder;
      if (newFolderName.trim()) {
        const cr = await axios.post(`${API}/site-folders/`, { site: quote.site, name: newFolderName.trim() });
        folder = cr.data;
      } else if (selectedFolderId) {
        folder = siteFolders.find((f) => f.id === Number(selectedFolderId));
      }
      if (folder) {
        const existing = (folder.documents || []).find((d) => d.name === fileName);
        if (existing) await axios.delete(`${API}/site-documents/${existing.id}/`);
        const pdfFile = new File([blob], fileName, { type: "application/pdf" });
        const fd = new FormData();
        fd.append("file", pdfFile);
        fd.append("folder", folder.id);
        await axios.post(`${API}/site-documents/`, fd);
      }
    } catch (e) {
      alert("Failed: " + e.message);
    } finally {
      setFilingPDF(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setStatusUpdating(true);
    try {
      await axios.patch(`${API}/general-quotes/${quote.id}/`, { status: newStatus });
      load();
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleCreateRequest = async () => {
    if (!assignedTo) return;
    setCreatingRequest(true);
    try {
      const r = await axios.post(`${API}/general-service-requests/`, {
        quote: quote.id,
        assigned_to: Number(assignedTo),
      });
      onRequestCreated(r.data.id);
    } finally {
      setCreatingRequest(false);
    }
  };

  const handleDelete = async () => {
    const msg = quote.has_request
      ? "Delete this quote? The linked service request will also be deleted. This cannot be undone."
      : "Delete this quote? This cannot be undone.";
    if (!window.confirm(msg)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/general-quotes/${quote.id}/`);
      onBack();
    } catch (e) {
      alert("Delete failed: " + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
      setDeleting(false);
    }
  };

  const handleEdit = () => {
    setEditTitle(quote.title || "");
    const map = {};
    (quote.items || []).forEach((it) => {
      map[it.id] = {
        description: it.description || "",
        proposed_solution: it.proposed_solution || "",
        quantity: it.quantity || "1",
        unit_price_ex_gst: it.unit_price_ex_gst || "",
      };
    });
    setEditItems(map);
    setEditNewItems([]);
    setEditMode(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/general-quotes/${quote.id}/`, { title: editTitle });
      await Promise.all(
        Object.entries(editItems).map(([id, vals]) => {
          const qty = parseFloat(vals.quantity) || 1;
          const unitPrice = parseFloat(vals.unit_price_ex_gst) || 0;
          return axios.patch(`${API}/general-quote-items/${id}/`, {
            description: vals.description,
            proposed_solution: vals.proposed_solution,
            quantity: qty,
            unit_price_ex_gst: unitPrice,
            price_ex_gst: qty * unitPrice,
          });
        })
      );
      for (const item of editNewItems.filter((it) => it.description.trim())) {
        const qty = parseFloat(item.quantity) || 1;
        const unitPrice = parseFloat(item.unit_price_ex_gst) || 0;
        await axios.post(`${API}/general-quote-items/`, {
          quote: quote.id,
          description: item.description,
          proposed_solution: item.proposed_solution,
          quantity: qty,
          unit_price_ex_gst: unitPrice,
          price_ex_gst: qty * unitPrice,
        });
      }
      setEditNewItems([]);
      setEditMode(false);
      load();
    } catch (e) {
      alert("Save failed: " + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    } finally {
      setSaving(false);
    }
  };

  const editTotalEx =
    Object.values(editItems).reduce((a, v) => a + (parseFloat(v.quantity) || 1) * (parseFloat(v.unit_price_ex_gst) || 0), 0) +
    editNewItems.reduce((a, v) => a + (parseFloat(v.quantity) || 1) * (parseFloat(v.unit_price_ex_gst) || 0), 0);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px", flexWrap: "wrap" }}>
        <BackButton onClick={onBack} />
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: C.navy }}>
            GQ-{String(quote.id).padStart(3, "0")} — {quote.site_name}
          </h2>
          {quote.title && !editMode && (
            <div style={{ fontSize: "15px", fontWeight: "700", color: C.navy, marginTop: "4px" }}>
              {quote.title}
            </div>
          )}
        </div>
        <StatusBadge status={quote.status} />
        {!editMode && (
          <button style={btn(C.slate, true)} onClick={handleEdit}>
            <LuPencil size={15} /> Edit
          </button>
        )}
        {!editMode && (
          <button style={btn(C.red, true)} onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Delete"}
          </button>
        )}
        <button style={btn(C.teal)} onClick={() => handleDownload(false)} disabled={filingPDF || editMode}>
          <LuFileDown size={15} /> Download PDF
        </button>
        <button style={btn(C.navy)} onClick={handlePDF} disabled={generatingPDF || filingPDF || editMode}>
          <LuFolderOpen size={15} /> {generatingPDF ? "Loading…" : filingPDF ? "Saving…" : "Save to Folder"}
        </button>
      </div>

      {/* Meta */}
      <div style={{ display: "flex", gap: "16px", marginBottom: "16px", flexWrap: "wrap" }}>
        <div style={{ fontSize: "13px", color: C.slate }}>
          <span style={{ fontWeight: "600", color: C.navy }}>Created:</span> {new Date(quote.created_at).toLocaleDateString("en-AU")}
        </div>
      </div>

      {/* Status actions */}
      {quote.status === "draft" && (
        <div style={{ marginBottom: "16px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button style={btn(C.teal, true)} onClick={() => handleStatusChange("sent")} disabled={statusUpdating}>Mark as Sent</button>
        </div>
      )}
      {quote.status === "sent" && (
        <div style={{ marginBottom: "16px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button style={btn(C.green)} onClick={() => handleStatusChange("approved")} disabled={statusUpdating}>Mark Approved</button>
          <button style={btn(C.red, true)} onClick={() => handleStatusChange("rejected")} disabled={statusUpdating}>Mark Rejected</button>
        </div>
      )}

      {/* Create Request */}
      {quote.status === "approved" && !quote.has_request && (
        <div style={{ backgroundColor: "#f0fdf4", border: `1px solid ${C.green}40`, borderRadius: "8px", padding: "16px", marginBottom: "20px", display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
          <LuClipboardCheck size={20} color={C.green} />
          <span style={{ fontSize: "14px", color: C.navy, fontWeight: "600", flex: 1 }}>
            Quote approved — create a Service Request to assign works to a technician.
          </span>
          <select
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            style={{ padding: "8px 10px", border: `1px solid ${C.border}`, borderRadius: "6px", fontSize: "13px" }}
          >
            <option value="">— Assign tech —</option>
            {techs.map((t) => (
              <option key={t.id} value={t.id}>
                {[t.first_name, t.last_name].filter(Boolean).join(" ") || t.username}
              </option>
            ))}
          </select>
          <button style={btn(C.green)} onClick={handleCreateRequest} disabled={!assignedTo || creatingRequest}>
            {creatingRequest ? "Creating…" : "Create Service Request"}
          </button>
        </div>
      )}

      {/* Edit title */}
      {editMode && (
        <div style={{ marginBottom: "16px" }}>
          <label style={{ fontSize: "13px", fontWeight: "600", color: C.navy, display: "block", marginBottom: "6px" }}>Description / Title</label>
          <input
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            style={{ width: "100%", padding: "8px 10px", border: `1px solid ${C.border}`, borderRadius: "6px", fontSize: "13px" }}
          />
        </div>
      )}

      {/* Items table */}
      <div style={{ overflowX: "auto", marginBottom: "20px" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ backgroundColor: C.navy, color: C.white }}>
              <th style={{ padding: "8px 10px", textAlign: "left" }}>#</th>
              <th style={{ padding: "8px 10px", textAlign: "left" }}>Description</th>
              <th style={{ padding: "8px 10px", textAlign: "left" }}>Proposed Solution</th>
              <th style={{ padding: "8px 10px", textAlign: "right" }}>Qty</th>
              <th style={{ padding: "8px 10px", textAlign: "right" }}>Unit Price</th>
              <th style={{ padding: "8px 10px", textAlign: "right" }}>GST</th>
              <th style={{ padding: "8px 10px", textAlign: "right" }}>Total (inc. GST)</th>
            </tr>
          </thead>
          <tbody>
            {(quote.items || []).map((item, idx) => {
              const ev = editItems[item.id] || {};
              const qty = editMode ? (parseFloat(ev.quantity) || 1) : (parseFloat(item.quantity) || 1);
              const unitPrice = editMode ? (parseFloat(ev.unit_price_ex_gst) || 0) : (parseFloat(item.unit_price_ex_gst) || 0);
              const ex = qty * unitPrice;
              const itemGst = ex * 0.1;
              const itemTotal = ex * 1.1;
              return (
                <React.Fragment key={item.id}>
                <tr style={{ backgroundColor: idx % 2 === 0 ? C.white : C.bg }}>
                  <td style={{ padding: "10px", color: C.slate, verticalAlign: "top" }}>{idx + 1}</td>
                  <td style={{ padding: "10px", verticalAlign: "top" }}>
                    {editMode ? (
                      <textarea
                        value={ev.description || ""}
                        onChange={(e) => setEditItems((p) => ({ ...p, [item.id]: { ...p[item.id], description: e.target.value } }))}
                        rows={3}
                        style={{ width: "100%", padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: "4px", fontSize: "12px", resize: "vertical" }}
                      />
                    ) : (
                      <span style={{ color: C.navy, fontWeight: "500" }}>{item.description}</span>
                    )}
                  </td>
                  <td style={{ padding: "10px", verticalAlign: "top" }}>
                    {editMode ? (
                      <textarea
                        value={ev.proposed_solution || ""}
                        onChange={(e) => setEditItems((p) => ({ ...p, [item.id]: { ...p[item.id], proposed_solution: e.target.value } }))}
                        rows={3}
                        style={{ width: "100%", padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: "4px", fontSize: "12px", resize: "vertical" }}
                      />
                    ) : (
                      <span style={{ color: C.slate }}>{item.proposed_solution}</span>
                    )}
                  </td>
                  <td style={{ padding: "10px", textAlign: "right", verticalAlign: "top" }}>
                    {editMode ? (
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={ev.quantity || ""}
                        onChange={(e) => setEditItems((p) => ({ ...p, [item.id]: { ...p[item.id], quantity: e.target.value } }))}
                        style={{ width: "60px", padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: "4px", fontSize: "13px", textAlign: "right" }}
                      />
                    ) : (
                      <span style={{ color: C.slate }}>{Number(item.quantity).toLocaleString("en-AU", { maximumFractionDigits: 2 })}</span>
                    )}
                  </td>
                  <td style={{ padding: "10px", textAlign: "right", verticalAlign: "top" }}>
                    {editMode ? (
                      <div style={{ display: "flex", alignItems: "center", gap: "4px", justifyContent: "flex-end" }}>
                        <span style={{ color: C.slate }}>$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={ev.unit_price_ex_gst || ""}
                          onChange={(e) => setEditItems((p) => ({ ...p, [item.id]: { ...p[item.id], unit_price_ex_gst: e.target.value } }))}
                          style={{ width: "80px", padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: "4px", fontSize: "13px", textAlign: "right" }}
                        />
                      </div>
                    ) : (
                      <span style={{ color: C.navy }}>${fmt2(item.unit_price_ex_gst)}</span>
                    )}
                  </td>
                  <td style={{ padding: "10px", textAlign: "right", verticalAlign: "top", color: C.slate, whiteSpace: "nowrap" }}>
                    ${fmt2(itemGst)}
                  </td>
                  <td style={{ padding: "10px", textAlign: "right", verticalAlign: "top", fontWeight: "600", color: C.navy, whiteSpace: "nowrap" }}>
                    ${fmt2(itemTotal)}
                  </td>
                </tr>
                <tr key={`${item.id}-photos`}>
                  <td colSpan={7} style={{ padding: "4px 10px 10px 36px", backgroundColor: idx % 2 === 0 ? C.white : C.bg }}>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                      {(item.photos || []).map((ph) => (
                        <div key={ph.id} style={{ position: "relative", flexShrink: 0 }}>
                          <img
                            src={ph.photo_url}
                            alt=""
                            style={{ width: "64px", height: "64px", objectFit: "cover", borderRadius: "4px", border: `1px solid ${C.border}`, display: "block" }}
                          />
                          <button
                            onClick={() => handleDeleteItemPhoto(ph.id)}
                            style={{ position: "absolute", top: -5, right: -5, width: "16px", height: "16px", borderRadius: "50%", background: C.red, border: "none", color: C.white, fontSize: "11px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, lineHeight: 1 }}
                            title="Remove photo"
                          >×</button>
                        </div>
                      ))}
                      <label style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", color: C.slate, padding: "4px 8px", border: `1px dashed ${C.border}`, borderRadius: "4px", flexShrink: 0 }}>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          style={{ display: "none" }}
                          onChange={(e) => handleItemPhotoUpload(item.id, e)}
                        />
                        <LuCamera size={13} /> Add Photo
                      </label>
                    </div>
                  </td>
                </tr>
                </React.Fragment>
              );
            })}
            {editMode && editNewItems.map((item, idx) => {
              const ex = (parseFloat(item.quantity) || 1) * (parseFloat(item.unit_price_ex_gst) || 0);
              const itemGst = ex * 0.1;
              const itemTotal = ex * 1.1;
              const rowIdx = (quote.items || []).length + idx;
              return (
                <tr key={`new-${idx}`} style={{ backgroundColor: rowIdx % 2 === 0 ? C.white : C.bg }}>
                  <td style={{ padding: "10px", color: C.slate, verticalAlign: "top" }}>{rowIdx + 1}</td>
                  <td style={{ padding: "10px", verticalAlign: "top" }}>
                    <textarea
                      value={item.description}
                      onChange={(e) => setEditNewItems((p) => p.map((it, i) => i === idx ? { ...it, description: e.target.value } : it))}
                      rows={3}
                      placeholder="Describe the work…"
                      style={{ width: "100%", padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: "4px", fontSize: "12px", resize: "vertical" }}
                    />
                  </td>
                  <td style={{ padding: "10px", verticalAlign: "top" }}>
                    <textarea
                      value={item.proposed_solution}
                      onChange={(e) => setEditNewItems((p) => p.map((it, i) => i === idx ? { ...it, proposed_solution: e.target.value } : it))}
                      rows={3}
                      placeholder="Proposed approach…"
                      style={{ width: "100%", padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: "4px", fontSize: "12px", resize: "vertical" }}
                    />
                  </td>
                  <td style={{ padding: "10px", textAlign: "right", verticalAlign: "top" }}>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.quantity}
                      onChange={(e) => setEditNewItems((p) => p.map((it, i) => i === idx ? { ...it, quantity: e.target.value } : it))}
                      style={{ width: "60px", padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: "4px", fontSize: "13px", textAlign: "right" }}
                    />
                  </td>
                  <td style={{ padding: "10px", textAlign: "right", verticalAlign: "top" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "4px", justifyContent: "flex-end" }}>
                      <span style={{ color: C.slate }}>$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price_ex_gst}
                        onChange={(e) => setEditNewItems((p) => p.map((it, i) => i === idx ? { ...it, unit_price_ex_gst: e.target.value } : it))}
                        placeholder="0.00"
                        style={{ width: "80px", padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: "4px", fontSize: "13px", textAlign: "right" }}
                      />
                    </div>
                  </td>
                  <td style={{ padding: "10px", textAlign: "right", verticalAlign: "top", color: C.slate, whiteSpace: "nowrap" }}>
                    ${fmt2(itemGst)}
                  </td>
                  <td style={{ padding: "10px", textAlign: "right", verticalAlign: "top", fontWeight: "600", color: C.navy, whiteSpace: "nowrap" }}>
                    ${fmt2(itemTotal)}
                  </td>
                  <td style={{ padding: "10px", verticalAlign: "top" }}>
                    <button
                      onClick={() => setEditNewItems((p) => p.filter((_, i) => i !== idx))}
                      style={{ background: "none", border: "none", cursor: "pointer", color: C.red, padding: "4px" }}
                    >
                      <LuTrash2 size={15} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editMode && (
        <button
          style={{ ...btn(C.teal, true), marginBottom: "16px" }}
          onClick={() => setEditNewItems((p) => [...p, emptyItem()])}
        >
          <LuPlus size={14} /> Add Item
        </button>
      )}

      {/* Totals */}
      {(() => {
        const totalEx = editMode ? editTotalEx : Number(quote.total_ex_gst);
        const gst = totalEx * 0.1;
        const totalInc = totalEx * 1.1;
        return (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
            <div style={{ border: `1px solid ${C.border}`, borderRadius: "8px", overflow: "hidden", minWidth: "260px" }}>
              {[
                { label: "Subtotal (ex. GST)", value: `$${fmt2(totalEx)}` },
                { label: "GST (10%)", value: `$${fmt2(gst)}` },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "8px 14px", borderBottom: `1px solid ${C.border}`, fontSize: "13px" }}>
                  <span style={{ color: C.slate }}>{label}</span>
                  <span style={{ color: C.navy, fontWeight: "600" }}>{value}</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", backgroundColor: C.navy }}>
                <span style={{ color: C.white, fontWeight: "700", fontSize: "14px" }}>TOTAL (inc. GST)</span>
                <span style={{ color: C.white, fontWeight: "700", fontSize: "14px" }}>${fmt2(totalInc)}</span>
              </div>
            </div>
          </div>
        );
      })()}

      {editMode && (
        <div style={{ display: "flex", gap: "10px", marginTop: "8px" }}>
          <button style={btn(C.slate, true)} onClick={() => { setEditMode(false); setEditNewItems([]); }} disabled={saving}>Cancel</button>
          <button style={btn(C.navy)} onClick={handleSaveEdit} disabled={saving}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      )}

      {/* Attachments */}
      <div style={{ marginTop: "32px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
          <div style={{ fontSize: "14px", fontWeight: "700", color: C.navy }}>Attachments</div>
          {!editMode && (
            <>
              <input
                type="file"
                multiple
                accept=".pdf,image/*,.doc,.docx,.xls,.xlsx"
                ref={attachmentInputRef}
                style={{ display: "none" }}
                onChange={handleAttachmentUpload}
              />
              <button style={btn(C.slate, true)} onClick={() => attachmentInputRef.current?.click()} disabled={uploadingAttachment}>
                <LuUpload size={14} /> {uploadingAttachment ? "Uploading…" : "Add Files"}
              </button>
            </>
          )}
        </div>

        {(quote.attachments || []).length === 0 && !uploadingAttachment && (
          <div style={{ fontSize: "13px", color: C.slate, fontStyle: "italic" }}>No attachments yet.</div>
        )}

        {uploadingAttachment && (
          <div style={{ fontSize: "13px", color: C.slate }}>Uploading…</div>
        )}

        {(quote.attachments || []).map((att) => (
          <div key={att.id} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
            <LuPaperclip size={14} style={{ color: C.slate, flexShrink: 0 }} />
            <a
              href={att.file_url}
              target="_blank"
              rel="noreferrer"
              style={{ flex: 1, fontSize: "13px", color: C.blue, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {att.name}
            </a>
            <span style={{ fontSize: "12px", color: C.slate, flexShrink: 0 }}>
              {att.file_size
                ? att.file_size > 1048576
                  ? (att.file_size / 1048576).toFixed(1) + " MB"
                  : Math.round(att.file_size / 1024) + " KB"
                : ""}
            </span>
            {!editMode && (
              <button
                style={{ background: "none", border: "none", cursor: "pointer", padding: "2px", color: C.slate }}
                onClick={() => handleDeleteAttachment(att.id)}
                title="Remove"
              >
                <LuTrash2 size={14} />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Folder picker modal */}
      {showFileModal && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ backgroundColor: C.white, borderRadius: "12px", padding: "28px", width: "440px", maxWidth: "92vw", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
            <h3 style={{ margin: "0 0 20px", fontSize: "16px", fontWeight: "700", color: C.navy }}>Save Quotation PDF</h3>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ fontSize: "13px", fontWeight: "600", color: C.navy, display: "block", marginBottom: "6px" }}>
                File to existing folder
              </label>
              <select
                value={selectedFolderId}
                onChange={(e) => { setSelectedFolderId(e.target.value); setNewFolderName(""); }}
                style={{ width: "100%", padding: "8px 10px", border: `1px solid ${C.border}`, borderRadius: "6px", fontSize: "13px" }}
              >
                <option value="">— Don't file —</option>
                {siteFolders.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
              <div style={{ flex: 1, height: "1px", backgroundColor: C.border }} />
              <span style={{ fontSize: "12px", color: C.slate }}>or create new folder</span>
              <div style={{ flex: 1, height: "1px", backgroundColor: C.border }} />
            </div>

            <div style={{ marginBottom: "24px" }}>
              <label style={{ fontSize: "13px", fontWeight: "600", color: C.navy, display: "block", marginBottom: "6px" }}>
                New folder name
              </label>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => { setNewFolderName(e.target.value); setSelectedFolderId(""); }}
                placeholder="e.g. Quotations 2026"
                style={{ width: "100%", padding: "8px 10px", border: `1px solid ${C.border}`, borderRadius: "6px", fontSize: "13px" }}
              />
            </div>

            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button style={btn(C.slate, true)} onClick={() => setShowFileModal(false)}>Cancel</button>
              <button
                style={btn(C.navy)}
                onClick={() => handleDownload(true)}
                disabled={!selectedFolderId && !newFolderName.trim()}
              >
                <LuFolderOpen size={14} /> Save to Folder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Request Detail ────────────────────────────────────────────────────────────

function RequestDetail({ requestId, onBack }) {
  const [request, setRequest] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/general-service-requests/${requestId}/`).then((r) => setRequest(r.data));
  }, [requestId]);

  useEffect(() => { load(); }, [load]);

  if (!request) return <div style={{ padding: "40px", color: C.slate }}>Loading…</div>;

  const quote = request.quote_detail || {};

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px", flexWrap: "wrap" }}>
        <BackButton onClick={onBack} />
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: C.navy }}>
            GR-{String(request.id).padStart(3, "0")} — {quote.site_name}
          </h2>
          {quote.title && (
            <div style={{ fontSize: "15px", fontWeight: "700", color: C.navy, marginTop: "4px" }}>
              {quote.title}
            </div>
          )}
        </div>
        <StatusBadge status={request.status} />
      </div>

      <div style={{ display: "flex", gap: "16px", marginBottom: "20px", flexWrap: "wrap" }}>
        <div style={{ fontSize: "13px", color: C.slate }}>
          <span style={{ fontWeight: "600", color: C.navy }}>Assigned to:</span> {request.assigned_to_name}
        </div>
        <div style={{ fontSize: "13px", color: C.slate }}>
          <span style={{ fontWeight: "600", color: C.navy }}>Created:</span> {new Date(request.created_at).toLocaleDateString("en-AU")}
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ backgroundColor: C.navy, color: C.white }}>
              <th style={{ padding: "8px 10px", textAlign: "left" }}>#</th>
              <th style={{ padding: "8px 10px", textAlign: "left" }}>Description</th>
              <th style={{ padding: "8px 10px", textAlign: "left" }}>Actions Taken</th>
              <th style={{ padding: "8px 10px", textAlign: "left" }}>Photo</th>
              <th style={{ padding: "8px 10px", textAlign: "center" }}>Attested</th>
            </tr>
          </thead>
          <tbody>
            {(request.items || []).map((item, idx) => {
              const qi = item.quote_item_detail || {};
              return (
                <tr key={item.id} style={{ backgroundColor: idx % 2 === 0 ? C.white : C.bg }}>
                  <td style={{ padding: "10px", color: C.slate, verticalAlign: "top" }}>{idx + 1}</td>
                  <td style={{ padding: "10px", verticalAlign: "top" }}>
                    <div style={{ fontWeight: "600", color: C.navy }}>{qi.description}</div>
                    {qi.proposed_solution && (
                      <div style={{ color: C.slate, fontSize: "12px", marginTop: "2px" }}>{qi.proposed_solution}</div>
                    )}
                  </td>
                  <td style={{ padding: "10px", color: C.slate, fontSize: "13px", verticalAlign: "top" }}>
                    {item.actions_taken || <span style={{ fontStyle: "italic" }}>Not yet filled</span>}
                  </td>
                  <td style={{ padding: "10px", verticalAlign: "top" }}>
                    {item.photo_url ? (
                      <img src={item.photo_url} alt="completion" style={{ width: "80px", height: "60px", objectFit: "cover", borderRadius: "4px" }} />
                    ) : (
                      <span style={{ color: C.slate, fontSize: "12px", fontStyle: "italic" }}>None</span>
                    )}
                  </td>
                  <td style={{ padding: "10px", textAlign: "center", verticalAlign: "top" }}>
                    {item.attested
                      ? <span style={{ color: C.green, fontWeight: "700", fontSize: "14px" }}>✓</span>
                      : <span style={{ color: C.slate, fontSize: "12px" }}>Pending</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function GeneralQuotesPage() {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState("quotes");
  const [view, setView] = useState("list");
  const [quotes, setQuotes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [pendingAutoFile, setPendingAutoFile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(null);

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/general-quotes/`),
      axios.get(`${API}/general-service-requests/`),
      axios.get(`${API}/sites/`),
    ]).then(([qr, rr, sr]) => {
      setQuotes(qr.data || []);
      setRequests(rr.data || []);
      setSites(sr.data || []);
    }).catch((err) => {
      console.error("General Quotes load error:", err.response?.data || err.message);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (view === "new-quote") {
    return (
      <div style={{ padding: isMobile ? "16px" : "24px", maxWidth: "1100px", margin: "0 auto" }}>
        <QuoteForm
          sites={sites}
          onSaved={(id) => { loadData(); setSelectedId(id); setPendingAutoFile(true); setView("quote-detail"); setTab("quotes"); }}
          onCancel={() => setView("list")}
        />
      </div>
    );
  }

  if (view === "quote-detail" && selectedId) {
    return (
      <div style={{ padding: isMobile ? "16px" : "24px", maxWidth: "1100px", margin: "0 auto" }}>
        <QuoteDetail
          quoteId={selectedId}
          autoFile={pendingAutoFile}
          onAutoFileDone={() => setPendingAutoFile(false)}
          onBack={() => { loadData(); setView("list"); }}
          onRequestCreated={(id) => { loadData(); setSelectedId(id); setView("request-detail"); setTab("open"); }}
        />
      </div>
    );
  }

  if (view === "request-detail" && selectedId) {
    return (
      <div style={{ padding: isMobile ? "16px" : "24px", maxWidth: "1100px", margin: "0 auto" }}>
        <RequestDetail
          requestId={selectedId}
          onBack={() => setView("list")}
        />
      </div>
    );
  }

  const openRequests = requests.filter((r) => r.status !== "completed");
  const completedRequests = requests.filter((r) => r.status === "completed");

  return (
    <div>
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>Quotations</div>
        {tab === "quotes" && (
          <button style={styles.addButton} onClick={() => setView("new-quote")}>
            <LuPlus size={15} /> New Quote
          </button>
        )}
      </div>
      <div style={styles.content}>
        <div style={styles.card}>
          {/* Tab nav */}
          <div style={{ display: "flex", borderBottom: `1px solid ${colors.border}`, padding: "0 20px" }}>
            {[["open", "Open Requests"], ["quotes", "Quotes"], ["completed", "Completed"]].map(([id, label]) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                style={{
                  padding: "12px 16px",
                  fontSize: "13px",
                  fontWeight: "600",
                  border: "none",
                  backgroundColor: "transparent",
                  cursor: "pointer",
                  color: tab === id ? colors.text : "#64748b",
                  borderBottom: tab === id ? `2px solid ${colors.activeBlue}` : "2px solid transparent",
                  marginBottom: "-1px",
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {loading ? (
            <div style={styles.loading}>Loading…</div>
          ) : tab === "quotes" ? (
            quotes.length === 0 ? (
              <div style={styles.loading}>No quotations yet. Click "New Quote" to create one.</div>
            ) : (
              <>
                <div style={{ ...styles.tableHeader, gridTemplateColumns: "80px 2fr 2fr 1fr 1fr" }}>
                  <div>Ref</div>
                  <div>Site</div>
                  <div>Description</div>
                  <div>Status</div>
                  <div>Total inc. GST</div>
                </div>
                {quotes.map((q) => (
                  <div
                    key={q.id}
                    style={{ ...styles.tableRow(hovered === q.id), gridTemplateColumns: "80px 2fr 2fr 1fr 1fr" }}
                    onMouseEnter={() => setHovered(q.id)}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => { setSelectedId(q.id); setView("quote-detail"); }}
                  >
                    <div style={{ fontWeight: "600", color: colors.text }}>GQ-{String(q.id).padStart(3, "0")}</div>
                    <div style={{ fontWeight: "500" }}>{q.site_name}</div>
                    <div style={{ color: "#64748b" }}>{q.title || <span style={{ fontStyle: "italic" }}>—</span>}</div>
                    <div><StatusBadge status={q.status} /></div>
                    <div style={{ fontWeight: "600" }}>
                      ${Number(q.total_inc_gst).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                ))}
                <div style={styles.count}>{quotes.length} quote{quotes.length !== 1 ? "s" : ""}</div>
              </>
            )
          ) : tab === "open" ? (
            openRequests.length === 0 ? (
              <div style={styles.loading}>No open service requests.</div>
            ) : (
              <>
                <div style={{ ...styles.tableHeader, gridTemplateColumns: "80px 2fr 2fr 1fr 1fr" }}>
                  <div>Ref</div>
                  <div>Site</div>
                  <div>Assigned To</div>
                  <div>Status</div>
                  <div>Created</div>
                </div>
                {openRequests.map((r) => (
                  <div
                    key={r.id}
                    style={{ ...styles.tableRow(hovered === r.id), gridTemplateColumns: "80px 2fr 2fr 1fr 1fr" }}
                    onMouseEnter={() => setHovered(r.id)}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => { setSelectedId(r.id); setView("request-detail"); }}
                  >
                    <div style={{ fontWeight: "600", color: colors.text }}>GR-{String(r.id).padStart(3, "0")}</div>
                    <div style={{ fontWeight: "500" }}>{r.quote_detail?.site_name || "—"}</div>
                    <div style={{ color: "#64748b" }}>{r.assigned_to_name || "—"}</div>
                    <div><StatusBadge status={r.status} /></div>
                    <div style={{ color: "#64748b" }}>{new Date(r.created_at).toLocaleDateString("en-AU")}</div>
                  </div>
                ))}
                <div style={styles.count}>{openRequests.length} request{openRequests.length !== 1 ? "s" : ""}</div>
              </>
            )
          ) : (
            completedRequests.length === 0 ? (
              <div style={styles.loading}>No completed service requests yet.</div>
            ) : (
              <>
                <div style={{ ...styles.tableHeader, gridTemplateColumns: "80px 2fr 2fr 1fr 1fr" }}>
                  <div>Ref</div>
                  <div>Site</div>
                  <div>Assigned To</div>
                  <div>Status</div>
                  <div>Created</div>
                </div>
                {completedRequests.map((r) => (
                  <div
                    key={r.id}
                    style={{ ...styles.tableRow(hovered === r.id), gridTemplateColumns: "80px 2fr 2fr 1fr 1fr" }}
                    onMouseEnter={() => setHovered(r.id)}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => { setSelectedId(r.id); setView("request-detail"); }}
                  >
                    <div style={{ fontWeight: "600", color: colors.text }}>GR-{String(r.id).padStart(3, "0")}</div>
                    <div style={{ fontWeight: "500" }}>{r.quote_detail?.site_name || "—"}</div>
                    <div style={{ color: "#64748b" }}>{r.assigned_to_name || "—"}</div>
                    <div><StatusBadge status={r.status} /></div>
                    <div style={{ color: "#64748b" }}>{new Date(r.created_at).toLocaleDateString("en-AU")}</div>
                  </div>
                ))}
                <div style={styles.count}>{completedRequests.length} request{completedRequests.length !== 1 ? "s" : ""}</div>
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
}
